import React, { useState } from 'react';
import { ArrowLeft, Flower2, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const RoseCopyPaste = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const roses = '🌹'.repeat(1000);

  const handleCopy = () => {
    navigator.clipboard.writeText(roses);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Structured data for rich snippets
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": `Rose Emoji Copy and Paste [${currentYear}]`,
    "description": `Copy and paste beautiful rose emojis 🌹. Updated for ${currentYear} with the latest designs. Perfect for expressing love, romance, and appreciation!`,
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1832",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I copy the rose emoji?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Simply click the 'Copy Rose Emoji' button and it will be copied to your clipboard. You can then paste it anywhere using Ctrl+V (Windows) or Cmd+V (Mac)."
        }
      },
      {
        "@type": "Question",
        "name": "Does the rose emoji work everywhere?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! The rose emoji works on all major platforms including Instagram, Facebook, WhatsApp, Twitter, TikTok, and more. It's compatible with both mobile and desktop devices."
        }
      },
      {
        "@type": "Question",
        "name": "What does the rose emoji symbolize?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The rose emoji 🌹 symbolizes love, romance, beauty, and passion. It's commonly used for expressing romantic feelings, appreciation, and celebrating special occasions."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Rose Emoji Copy and Paste 🌹 [{currentYear}] | #1 Rose Collection</title>
        <meta name="description" content={`【INSTANT COPY】 Get the beautiful rose emoji 🌹 One-click copy & paste ⚡ Works everywhere! Updated for ${currentYear} ✨ Perfect for expressing love and romance! #1 Rose Emoji Resource`} />
        <link rel="canonical" href="https://emojicollection.net/rose-copy-paste" />
        
        <meta property="og:title" content={`Rose Emoji Copy and Paste 🌹 [${currentYear}] | #1 Rose Collection`} />
        <meta property="og:description" content={`Get the beautiful rose emoji 🌹 One-click copy & paste ⚡ Works everywhere! Updated for ${currentYear} ✨ Express love and romance!`} />
        <meta property="og:url" content="https://emojicollection.net/rose-copy-paste" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1548094990-c16ca90f1f0d?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Rose Emoji Copy and Paste 🌹 [${currentYear}] | #1 Rose Collection`} />
        <meta name="twitter:description" content={`Get the beautiful rose emoji 🌹 One-click copy & paste ⚡ Works everywhere! Updated for ${currentYear} ✨ Express love and romance!`} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1548094990-c16ca90f1f0d?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="keywords" content="rose emoji copy paste, rose emoji, red rose emoji, flower emoji, love emoji, romantic emoji, rose symbol, instagram rose emoji" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Emoji Collection" />
        <meta name="revisit-after" content="1 days" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <img 
                src="https://images.unsplash.com/photo-1548094990-c16ca90f1f0d?auto=format&fit=crop&w=1200&q=80"
                alt="Beautiful red rose symbolizing the rose emoji"
                className="w-full h-48 object-cover rounded-xl mb-8"
              />
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-500 mb-4">
                <Flower2 className="w-8 h-8" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Rose Emoji Copy and Paste 🌹 [{currentYear}]
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Get instant access to the beautiful rose emoji! Perfect for expressing love, romance, and appreciation. Updated for {currentYear} with the latest design. One-click copy and paste feature!
              </p>
            </header>

            <section className="max-w-2xl mx-auto mb-12">
              <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-lg p-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Flower2 className="w-6 h-6 text-red-500 mr-2" />
                  Copy Rose Emoji Instantly
                </h2>
                <div className="mb-6 overflow-auto max-h-96 text-center text-6xl bg-white p-6 rounded-lg shadow-inner">
                  🌹
                </div>
                <button
                  onClick={handleCopy}
                  className={`w-full inline-flex items-center justify-center px-6 py-4 rounded-lg text-lg font-medium transition-all transform hover:scale-105 ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-red-500 to-pink-500 text-white hover:from-red-600 hover:to-pink-600'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="mr-2" size={24} />
                      Copied Rose Emoji!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2" size={24} />
                      Copy Rose Emoji
                    </>
                  )}
                </button>
              </div>
            </section>

            <section className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-semibold mb-6">Rose Emoji Meaning & Usage</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Symbolism</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>❤️ Love and Romance</li>
                    <li>✨ Beauty and Grace</li>
                    <li>💝 Passion and Desire</li>
                    <li>🙏 Gratitude and Appreciation</li>
                    <li>👑 Elegance and Sophistication</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-4">Perfect For</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>💌 Love Messages</li>
                    <li>🎉 Special Occasions</li>
                    <li>💑 Romantic Moments</li>
                    <li>🎂 Celebrations</li>
                    <li>🌟 Social Media Posts</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-6">Platform Compatibility</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium mb-4">Mobile Devices</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>✓ iPhone & iPad (iOS)</li>
                    <li>✓ Android phones & tablets</li>
                    <li>✓ Samsung devices</li>
                    <li>✓ All mobile browsers</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium mb-4">Desktop Platforms</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>✓ Windows (Chrome, Firefox, Edge)</li>
                    <li>✓ macOS (Safari, Chrome)</li>
                    <li>✓ Linux systems</li>
                    <li>✓ All modern browsers</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-6">Popular Uses</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-3">Romance</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Love messages</li>
                    <li>Valentine's Day</li>
                    <li>Anniversaries</li>
                    <li>Date nights</li>
                    <li>Wedding content</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-3">Social Media</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Instagram posts</li>
                    <li>Facebook updates</li>
                    <li>Twitter messages</li>
                    <li>TikTok captions</li>
                    <li>YouTube comments</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-3">Messaging</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>WhatsApp chats</li>
                    <li>Messenger texts</li>
                    <li>iMessage</li>
                    <li>Telegram</li>
                    <li>SMS messages</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-6">Frequently Asked Questions</h2>
              <div className="space-y-4">
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">How do I copy the rose emoji?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Simply click the "Copy Rose Emoji" button above, and the rose emoji will be copied to your clipboard. Then paste it anywhere using Ctrl+V (Windows) or Cmd+V (Mac).
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Does the rose emoji work everywhere?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Yes! The rose emoji is universal and works on all major platforms including Instagram, Facebook, WhatsApp, and more. It's compatible with both mobile and desktop devices.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">What does the rose emoji symbolize?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    The rose emoji 🌹 symbolizes love, romance, beauty, and passion. It's commonly used for expressing romantic feelings, appreciation, and celebrating special occasions.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Is this emoji free to use?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Absolutely! The rose emoji is completely free to use for both personal and commercial purposes. No attribution required.
                  </div>
                </details>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-6">Why Choose Our Rose Emoji</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p>
                  Our rose emoji copy and paste tool provides the most beautiful and universally compatible rose emoji available. The emoji is carefully optimized to display perfectly across all platforms and devices.
                </p>
                <p>
                  Whether you're expressing love, celebrating special moments, or adding elegance to your messages, our rose emoji helps you convey your feelings perfectly. With our instant copy feature, you can easily share this beautiful symbol across any platform or device.
                </p>
                <p>
                  The rose emoji is regularly updated to ensure you always have access to the latest design. Start spreading love and beauty with our rose emoji today!
                </p>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default RoseCopyPaste;